import { stringifyValues } from '@/lib/utils/queries'
import { extractZodIssues } from '@/lib/utils/zodErrorHelpers'
import { getApi } from '@/stores/api'
import { formatDateOnly } from '@/utils/DateOnlyHelpers'
import {
  CLR_KINDS,
  CLR_STATUSES,
  isClrErrorType,
  type ClrErrorType,
  type ClrSelectableRiskClassCode,
} from '@repo/constants'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { z } from 'zod'

// Deep links (emails, hand-typed URLs) can carry a scalar (`?erp=50317348`, JSON-parsed by the
// router into a number) where the app serializes an array — normalize instead of crashing.
const toStringArray = (value: unknown) =>
  value === undefined ? undefined : (Array.isArray(value) ? value : [value]).map(String)

export const creditLimitReviewsSearchSchema = z.object({
  keyword: z.string().optional(),
  erp: z.preprocess(toStringArray, z.array(z.string()).optional()),
  status: z.preprocess(toStringArray, z.array(z.enum(CLR_STATUSES)).optional()),
  kind: z.preprocess(toStringArray, z.array(z.enum(CLR_KINDS)).optional()),
  involvement: z.literal('mine').optional().catch(undefined),
  createdFrom: z.coerce.date().optional(),
  createdTo: z.coerce.date().optional(),
  // Deep link to one specific review (from the Review Events table's event → CLR link). There is
  // no per-review page, so this narrows the list to the single row instead.
  reviewId: z.number().optional(),
  page: z.number().optional(),
  pageSize: z.number().optional(),
})
export type CreditLimitReviewsSearch = z.infer<typeof creditLimitReviewsSearchSchema>

type CreditLimitReviewsQueries = Omit<CreditLimitReviewsSearch, 'createdFrom' | 'createdTo'> & {
  createdFrom?: string
  createdTo?: string
  timezone: string
  page: number
  pageSize: number
}

async function getCreditLimitReviews(queries: CreditLimitReviewsQueries, signal?: AbortSignal) {
  const res = await getApi().credit['credit-limit-reviews'].$get(
    // `stringifyValues` widens every key to optional; `timezone` is always present, so restore it.
    { query: { ...stringifyValues(queries), timezone: queries.timezone } },
    { init: { signal } },
  )
  return await res.json()
}

export type CreditLimitReviewRow = Awaited<ReturnType<typeof getCreditLimitReviews>>['rows'][number]

export function useCreditLimitReviews(search: CreditLimitReviewsSearch & { pageSize: number }) {
  const queries: CreditLimitReviewsQueries = {
    keyword: search.keyword,
    erp: search.erp,
    status: search.status,
    kind: search.kind,
    involvement: search.involvement,
    createdFrom: search.createdFrom ? formatDateOnly(search.createdFrom) : undefined,
    createdTo: search.createdTo ? formatDateOnly(search.createdTo) : undefined,
    reviewId: search.reviewId,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    page: search.page ?? 1,
    pageSize: search.pageSize,
  }

  return useQuery({
    queryKey: ['creditLimitReviews', queries],
    queryFn: ({ signal }) => getCreditLimitReviews(queries, signal),
  })
}

async function getCreditLimitReview(id: number, signal?: AbortSignal) {
  const res = await getApi().credit['credit-limit-reviews'][':id'].$get(
    { param: { id: String(id) } },
    { init: { signal } },
  )
  return await res.json()
}

export type CreditLimitReviewDetail = Awaited<ReturnType<typeof getCreditLimitReview>>
export type CreditLimitReviewVersion = CreditLimitReviewDetail['versions'][number]

/** Full review (versions, actions, evidences, viewer capabilities). `enabled` lets dialogs and
 *  sheets fetch lazily when they open. */
export function useCreditLimitReview(id: number | undefined, enabled = true) {
  return useQuery({
    queryKey: ['creditLimitReview', id],
    queryFn: ({ signal }) => getCreditLimitReview(id!, signal),
    enabled: enabled && id !== undefined,
  })
}

async function getSingleClrTask(id: number, signal?: AbortSignal) {
  const res = await getApi().credit['credit-limit-reviews'][':id'].task.$get(
    { param: { id: String(id) } },
    { init: { signal } },
  )
  const task = await res.json()

  // Same conversion the inbox feed does — the detail components take Dates, and one of the two
  // producers returning ISO strings would break them in only one of the two places they render.
  return {
    ...task,
    raisedAt: new Date(task.raisedAt),
    pendingSince: new Date(task.pendingSince),
    resolvedAt: task.resolvedAt ? new Date(task.resolvedAt) : null,
  }
}

/**
 * One CLR in the shape the inbox pane renders, for the standalone review page.
 *
 * Deliberately the inbox's own task rather than a second projection of the same review: the detail
 * components read a dozen-odd fields off it, and a page assembling that shape by hand would be a
 * second place for "what stage is this" and "is it resolved" to be decided.
 */
export function useSingleClrTask(id: number) {
  return useQuery({
    queryKey: ['singleClrTask', id],
    queryFn: ({ signal }) => getSingleClrTask(id, signal),
  })
}

async function getClrResolutionSnapshot(id: number, signal?: AbortSignal) {
  const res = await getApi().credit['credit-limit-reviews'][':id']['resolution-snapshot'].$get(
    { param: { id: String(id) } },
    { init: { signal } },
  )
  return await res.json()
}

export type ClrResolutionSnapshot = NonNullable<
  Awaited<ReturnType<typeof getClrResolutionSnapshot>>
>

/**
 * The master data a resolved CLR was decided against — payment terms, credit group, the review
 * dates, the multipliers, the new-account breakdown — captured when it went terminal.
 *
 * Resolves to `null`, not an error, for a review resolved before the snapshot table shipped. The
 * panel treats that as "no snapshot" and renders those fields live, which is what it did for every
 * review before this existed.
 *
 * `enabled` is the caller's terminal-status check: an in-review CLR has no snapshot to fetch, and
 * asking for one would be a request per inbox selection that could only ever answer null.
 */
export function useClrResolutionSnapshot(id: number | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ['clrResolutionSnapshot', id],
    queryFn: ({ signal }) => getClrResolutionSnapshot(id!, signal),
    enabled: enabled && id !== undefined,
  })
}

async function getClrEligibility(erp: string, signal?: AbortSignal) {
  const res = await getApi().credit['credit-limit-reviews'].eligibility.$get(
    { query: { erp } },
    { init: { signal } },
  )
  return await res.json()
}

export type ClrEligibility = Awaited<ReturnType<typeof getClrEligibility>>

/** Drives the "Prepare CLR" button on the customer detail page. */
export function useClrEligibility(erp: string) {
  return useQuery({
    queryKey: ['clrEligibility', erp],
    queryFn: ({ signal }) => getClrEligibility(erp, signal),
  })
}

async function getClrPrepareContext(erp: string, reviewId?: number, signal?: AbortSignal) {
  const res = await getApi().credit['credit-limit-reviews']['prepare-context'][':erp'].$get(
    { param: { erp }, query: reviewId === undefined ? {} : { reviewId: String(reviewId) } },
    { init: { signal } },
  )
  return await res.json()
}

export type ClrPrepareContext = Awaited<ReturnType<typeof getClrPrepareContext>>

/**
 * `reviewId` for an existing review (claim, rework): the context then carries the recommendation
 * stored on that review rather than the customer's live one, so the form agrees with the request
 * grid and the resubmit snapshot.
 */
export function useClrPrepareContext(erp: string, reviewId?: number) {
  return useQuery({
    queryKey: ['clrPrepareContext', erp, reviewId ?? null],
    queryFn: ({ signal }) => getClrPrepareContext(erp, reviewId, signal),
  })
}

export type ClrSubmissionPayload = {
  proposedCreditLimit: number
  proposedRiskClass: ClrSelectableRiskClassCode
  contextExplanation?: string
  rollingVarianceExplanation?: string
  financialRatiosExplanation?: string
  reasonForDeviation?: string
  evidencesExplanation?: string
  evidences: { fileKey: string; fileName: string; fileSize?: number }[]
}

/** Reads the translatable CLR business-rule key off a thrown error body, if any. */
export function extractClrErrorType(error: unknown): ClrErrorType | undefined {
  const issues = extractZodIssues(error)
  const type = (
    issues?.find((issue) => issue.code === 'custom')?.params as { type?: unknown } | undefined
  )?.type
  return isClrErrorType(type) ? type : undefined
}

/** Shared error toast: translated business rule when the backend sent one, generic otherwise. */
function useClrErrorToast() {
  const { t } = useTranslation('credit')
  return (error: unknown) => {
    const errorType = extractClrErrorType(error)
    toast.error(errorType ? t(`clr.errors.${errorType}`) : t('clr.toasts.error'))
  }
}

export function useCreateClr() {
  const queryClient = useQueryClient()
  const { t } = useTranslation('credit')
  const onError = useClrErrorToast()

  return useMutation({
    mutationFn: async (payload: ClrSubmissionPayload & { erp: string }) => {
      const res = await getApi().credit['credit-limit-reviews'].$post({ json: payload })
      return await res.json()
    },
    onSuccess: (_data, payload) => {
      queryClient.invalidateQueries({ queryKey: ['creditLimitReviews'] })
      queryClient.invalidateQueries({ queryKey: ['clrEligibility', payload.erp] })
      toast.success(t('clr.toasts.created'))
    },
    onError,
  })
}

export function useResubmitClr(id: number, erp: string) {
  const queryClient = useQueryClient()
  const { t } = useTranslation('credit')
  const onError = useClrErrorToast()

  return useMutation({
    mutationFn: async (payload: ClrSubmissionPayload) => {
      const res = await getApi().credit['credit-limit-reviews'][':id'].resubmit.$post({
        param: { id: String(id) },
        json: payload,
      })
      return await res.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['creditLimitReviews'] })
      // Stale without refetching YET: the prepare page still observes this query, and an
      // immediate refetch would flip viewer.canResubmit under it — flashing the "cannot be
      // edited" guard before the navigation away completes. The refetch is chained below instead.
      queryClient.invalidateQueries({ queryKey: ['creditLimitReview', id], refetchType: 'none' })
      queryClient.invalidateQueries({ queryKey: ['clrEligibility', erp] })
      // A resubmission can now be made from the inbox itself (`ReworkClrCard`), and it takes the
      // request out of the preparer's queue and back into the chain. Without these the pane would
      // go on offering the form for a review that is already back with its reviewer — the feed for
      // the inbox, the task for the standalone review page, neither of which is a prefix of the
      // keys above. Unlike the detail, both are safe to refetch: they drive banners and footers,
      // not the form's own guard.
      queryClient.invalidateQueries({ queryKey: ['credit', 'inbox'] })
      queryClient
        .invalidateQueries({ queryKey: ['singleClrTask', id] })
        // Then the viewer, once the stage has moved. The standalone review page reads its
        // authority from `viewer` and stays on the same review after a resubmission — nothing
        // navigates away from it — so leaving the detail merely stale left a preparer who is also
        // the reviewer looking at "Pending your review" with no decision footer until they
        // reloaded the page (UAT, Sep 2026). Chained after the task rather than fired beside it:
        // by then the request is back at `review`, the prepare form has come off on its own, and
        // there is no guard left for canResubmit to flash. Not awaited — the caller advances the
        // queue or closes the drawer in its own onSuccess and must not wait on either round trip.
        .then(() =>
          queryClient.refetchQueries({ queryKey: ['creditLimitReview', id], type: 'active' }),
        )
      toast.success(t('clr.toasts.resubmitted'))
    },
    onError,
  })
}
